<script setup>
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  records: {
    type: Array,
    required: true,
  },
  // Only a gap can be answered by writing text; needs-API and handover rows
  // are dismissed once looked at.
  canConvert: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['convert', 'dismiss']);

const { t } = useI18n();
const route = useRoute();

const EMPTY_MARK = '—';

const conversationLink = record =>
  record.conversation_display_id
    ? `/app/accounts/${route.params.accountId}/conversations/${record.conversation_display_id}`
    : null;
</script>

<template>
  <table
    class="w-full text-left border-collapse table-fixed"
    data-testid="unanswered-table"
  >
    <thead>
      <tr class="border-b border-n-strong">
        <th class="w-1/3 py-2 pr-2 text-sm font-medium text-n-slate-11">
          {{ $t('AI_AGENT.UNANSWERED.TABLE.QUESTION') }}
        </th>
        <th class="w-1/3 py-2 pr-2 text-sm font-medium text-n-slate-11">
          {{ $t('AI_AGENT.UNANSWERED.TABLE.BOT_ANSWER') }}
        </th>
        <th class="w-20 py-2 pr-2 text-sm font-medium text-n-slate-11">
          {{ $t('AI_AGENT.UNANSWERED.TABLE.CONFIDENCE') }}
        </th>
        <th class="w-28 py-2 pr-2 text-sm font-medium text-n-slate-11">
          {{ $t('AI_AGENT.UNANSWERED.TABLE.CONVERSATION') }}
        </th>
        <th class="py-2 text-sm font-medium text-n-slate-11" />
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="record in records"
        :key="record.id"
        class="border-b border-n-weak"
        :data-testid="`unanswered-row-${record.id}`"
      >
        <td class="py-2 pr-2 text-sm truncate text-n-slate-12">
          {{ record.question }}
        </td>
        <td class="py-2 pr-2 text-sm truncate text-n-slate-11">
          {{ record.bot_answer || EMPTY_MARK }}
        </td>
        <td class="py-2 pr-2 text-sm text-n-slate-11">
          {{
            record.confidence === null || record.confidence === undefined
              ? EMPTY_MARK
              : `${Math.round(record.confidence * 100)}%`
          }}
        </td>
        <td class="py-2 pr-2 text-sm">
          <a
            v-if="conversationLink(record)"
            :href="conversationLink(record)"
            class="text-n-brand hover:underline"
            :data-testid="`unanswered-conversation-${record.id}`"
          >
            {{ `#${record.conversation_display_id}` }}
          </a>
          <span v-else class="text-n-slate-10">{{ EMPTY_MARK }}</span>
        </td>
        <td class="py-2">
          <div class="flex items-center justify-end gap-1">
            <Button
              v-if="canConvert"
              sm
              faded
              :label="t('AI_AGENT.UNANSWERED.CONVERT')"
              :data-testid="`unanswered-convert-${record.id}`"
              @click="emit('convert', record)"
            />
            <Button
              v-tooltip="t('AI_AGENT.UNANSWERED.TABLE.DISMISS')"
              icon="i-lucide-x"
              slate
              sm
              faded
              :data-testid="`unanswered-dismiss-${record.id}`"
              @click="emit('dismiss', record)"
            />
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>
